// Exhibitor directory (v0.7) — three screens over the BUNDLED show data:
//   • Show directory  (#/show/{showKey})   — search a show's exhibitors
//   • Exhibitor detail (#/exhibitor/{key}) — cross-ref, capture/view, star
//   • Hit list        (#/hitlist)          — the shared team plan, all shows
//
// Everything here reads bundled data + IndexedDB, so the whole directory works with
// no signal. The only network touches are optional and non-blocking: a hit-list pull
// on open, and a push after a star.

import {
  loadShows,
  getShowDirectory,
  exhibitorKey,
  nameKey,
  fmtShowDates,
  type Exhibitor,
  type ShowDirectory,
} from './data';
import { listBoothsForShow } from '../db/booths';
import { star, unstar, starredKeys, listHitList } from '../db/hitlist';
import type { BoothRecord } from '../db/schema';
import { getSnapshot } from '../db/snapshot';
import type { VendorRow } from '../snapshot/types';
import { normalize, rankMatches } from '../vendors/match';
import { getIdentity, attributionName } from '../auth/auth';
import { setCapturePrefill } from '../capture/prefill';
import { syncNow, pullHitList } from '../sync/syncEngine';
import { chooseDialog } from '../ui/confirm';
import { toast } from '../ui/toast';

/**
 * Rows rendered at once. ASD is 1,119 exhibitors and this runs on a phone — past a
 * couple hundred rows the first paint gets visibly slow for a list nobody scrolls
 * to the bottom of. Over the cap we say so explicitly rather than silently truncate.
 */
const MAX_ROWS = 150;
const SEARCH_DEBOUNCE_MS = 150;

// ── Captured detection ──────────────────────────────────────────────────────

/**
 * Live booths for a show, indexed both ways so an exhibitor can be checked without
 * a per-booth DB round trip.
 *
 * Two lookups because two kinds of show exist in the data:
 *  - Shows WITH booth numbers (ASD/Closeout) match on the booth — the same
 *    `show + booth_number` identity the app already de-dups on, so a booth logged
 *    by hand from the Capture tab is found here too.
 *  - Shows WITHOUT them (WWIN/Evolve — booths aren't assigned this far out) have
 *    nothing to match on, so they fall back to an EXACT normalized vendor-name
 *    match. Deliberately exact, not fuzzy: a false ✓ would tell a buyer they'd
 *    already worked a booth they hadn't.
 */
interface CapturedIndex {
  byBooth: Map<string, BoothRecord[]>;
  byName: Map<string, BoothRecord[]>;
  hasBooths: boolean;
}

async function buildCapturedIndex(dir: ShowDirectory): Promise<CapturedIndex> {
  const booths = await listBoothsForShow(dir.show);
  const byBooth = new Map<string, BoothRecord[]>();
  const byName = new Map<string, BoothRecord[]>();
  for (const b of booths) {
    push(byBooth, b.booth_number.trim().toLowerCase(), b);
    const n = normalize(b.vendor_name);
    if (n) push(byName, n, b);
  }
  return { byBooth, byName, hasBooths: dir.has_booths };
}

function push<T>(map: Map<string, T[]>, key: string, value: T): void {
  if (!key) return;
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

interface CaptureState {
  /** Live booth records matching this exhibitor (empty = not captured). */
  records: BoothRecord[];
  /** Which of the exhibitor's booths are already logged. */
  capturedBooths: string[];
  captured: boolean;
  /** True when some — but not all — of a multi-booth exhibitor is done. */
  partial: boolean;
}

function captureStateFor(ex: Exhibitor, idx: CapturedIndex): CaptureState {
  const records: BoothRecord[] = [];
  const capturedBooths: string[] = [];

  if (idx.hasBooths && ex.booths.length) {
    for (const booth of ex.booths) {
      const hits = idx.byBooth.get(booth.trim().toLowerCase());
      if (hits?.length) {
        capturedBooths.push(booth);
        records.push(...hits);
      }
    }
  } else {
    records.push(...(idx.byName.get(normalize(ex.name)) ?? []));
  }

  return {
    records,
    capturedBooths,
    captured: records.length > 0,
    partial: ex.booths.length > 1 && capturedBooths.length > 0 && capturedBooths.length < ex.booths.length,
  };
}

// ── Show directory ──────────────────────────────────────────────────────────

export async function renderShowDirectory(root: HTMLElement, showKey: string): Promise<void> {
  const dir = await getShowDirectory(showKey);
  if (!dir) {
    root.innerHTML = `<div class="empty">Unknown show.<br/><a href="#/data" class="link">Back to Info</a></div>`;
    return;
  }
  if (dir.unavailable) {
    root.innerHTML = `
      <section class="card ex-dir">
        <a href="#/data" class="link">&lsaquo; Info</a>
        <h2 class="ex-dir__title">${esc(dir.label)}</h2>
        <div class="empty">Data unavailable.<br/><span class="ex-dir__why">${esc(dir.unavailable)}</span></div>
      </section>`;
    return;
  }

  const [idx, starred] = await Promise.all([buildCapturedIndex(dir), starredKeys()]);
  let query = '';

  root.innerHTML = `
    <section class="card ex-dir">
      <a href="#/data" class="link">&lsaquo; Info</a>
      <h2 class="ex-dir__title">${esc(dir.label)}</h2>
      <div class="ex-dir__meta">
        ${dir.count} exhibitors${dir.dates ? ` · ${esc(fmtShowDates(dir.dates))}` : ''}${dir.venue ? ` · ${esc(dir.venue)}` : ''}
      </div>
      ${
        dir.partial
          ? `<div class="ex-dir__partial">Partial list — ${esc(
              dir.has_booths ? 'more exhibitors may be added before the show.' : 'booth numbers aren’t assigned yet.',
            )}</div>`
          : ''
      }
      <div class="ex-dir__tools">
        <input type="search" id="ex-search" class="control"
               placeholder="Search ${dir.has_booths ? 'name or booth' : 'name'}&hellip;"
               aria-label="Search exhibitors" autocomplete="off" autocapitalize="none"
               autocorrect="off" spellcheck="false" />
        <a href="#/hitlist" class="ex-dir__hits" id="ex-hits">★ Hit list</a>
      </div>
      <div id="ex-body"></div>
    </section>
  `;

  const bodyEl = root.querySelector<HTMLElement>('#ex-body')!;
  const searchEl = root.querySelector<HTMLInputElement>('#ex-search')!;
  const hitsEl = root.querySelector<HTMLAnchorElement>('#ex-hits')!;

  async function refreshHitCount(): Promise<void> {
    const n = (await listHitList()).length;
    hitsEl.textContent = n ? `★ Hit list (${n})` : '★ Hit list';
  }

  function renderBody(): void {
    const q = query.trim().toLowerCase();
    const matched = q ? dir!.exhibitors.filter((e) => matches(e, q)) : dir!.exhibitors;

    if (!matched.length) {
      bodyEl.innerHTML = `<div class="empty empty--filtered">No exhibitor matches &ldquo;${esc(query)}&rdquo;.<br/>Try fewer letters.</div>`;
      return;
    }

    const shown = matched.slice(0, MAX_ROWS);
    const more = matched.length - shown.length;
    bodyEl.innerHTML =
      `<ul class="ex-list">${shown.map((e) => rowHtml(dir!, e, idx, starred)).join('')}</ul>` +
      (more > 0
        ? `<div class="ex-list__more">Showing ${shown.length} of ${matched.length} — keep typing to narrow it down.</div>`
        : '');

    wireStars(bodyEl, dir!, shown, starred, () => {
      renderBody();
      void refreshHitCount();
    });
  }

  let timer: number | undefined;
  searchEl.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      query = searchEl.value;
      renderBody();
    }, SEARCH_DEBOUNCE_MS);
  });

  renderBody();
  void refreshHitCount();

  // Non-blocking: pick up teammates' stars, then repaint if anything landed.
  void pullHitList().then(async (n) => {
    if (n > 0 && location.hash.startsWith('#/show/')) {
      const fresh = await starredKeys();
      starred.clear();
      fresh.forEach((k) => starred.add(k));
      renderBody();
      await refreshHitCount();
    }
  });
}

/** Substring match over the exhibitor's name AND any of its booth numbers. */
function matches(ex: Exhibitor, q: string): boolean {
  if (ex.name.toLowerCase().includes(q)) return true;
  return ex.booths.some((b) => b.toLowerCase().includes(q));
}

function rowHtml(dir: ShowDirectory, ex: Exhibitor, idx: CapturedIndex, starred: Set<string>): string {
  const key = exhibitorKey(dir.key, ex);
  const state = captureStateFor(ex, idx);
  const on = isStarred(dir.key, ex, starred);
  return `
    <li class="ex-row">
      <a class="ex-row__main" href="#/exhibitor/${encodeURIComponent(key)}">
        <div class="ex-row__name">${esc(ex.name)}</div>
        <div class="ex-row__meta">
          ${boothPill(ex)}
          ${
            state.captured
              ? `<span class="chip chip--captured">✓ ${state.partial ? 'Partly captured' : 'Captured'}</span>`
              : ''
          }
        </div>
      </a>
      <button type="button" class="ex-row__star${on ? ' ex-row__star--on' : ''}"
              data-key="${esc(key)}" aria-pressed="${on}"
              aria-label="${on ? 'Remove from' : 'Add to'} hit list">${on ? '★' : '☆'}</button>
    </li>`;
}

function boothPill(ex: Exhibitor): string {
  if (!ex.booths.length) return '';
  const extra = ex.booths.length - 1;
  return `<span class="chip chip--booth">${esc(ex.booths[0])}${extra > 0 ? ` +${extra}` : ''}</span>`;
}

/** A star matches on EITHER identity, so a data re-pull can't orphan it. */
function isStarred(showKey: string, ex: Exhibitor, starred: Set<string>): boolean {
  return starred.has(exhibitorKey(showKey, ex)) || starred.has(nameKey(showKey, ex));
}

function wireStars(
  scope: HTMLElement,
  dir: ShowDirectory,
  exhibitors: Exhibitor[],
  starred: Set<string>,
  onChange: () => void,
): void {
  const byKey = new Map(exhibitors.map((e) => [exhibitorKey(dir.key, e), e]));
  scope.querySelectorAll<HTMLButtonElement>('.ex-row__star').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      // The star sits inside a link-shaped row — don't navigate on a star tap.
      e.preventDefault();
      e.stopPropagation();
      const ex = byKey.get(btn.dataset.key ?? '');
      if (!ex) return;
      await toggleStar(dir, ex, starred);
      onChange();
    });
  });
}

/**
 * Star/un-star and update the in-memory key set so the caller can repaint without
 * re-reading IndexedDB. Writes land instantly and offline; the push is fire-and-
 * forget (syncNow no-ops when offline, and the star drains on the next trigger).
 */
async function toggleStar(dir: ShowDirectory, ex: Exhibitor, starred: Set<string>): Promise<void> {
  const key = exhibitorKey(dir.key, ex);
  const nKey = nameKey(dir.key, ex);
  const on = starred.has(key) || starred.has(nKey);

  if (on) {
    // Clear BOTH identities: the live row may have been created under either one
    // (e.g. starred before a re-pull changed the booth id).
    await unstar(key);
    if (nKey !== key) await unstar(nKey);
    starred.delete(key);
    starred.delete(nKey);
    toast(`Removed “${ex.name}” from the hit list`);
  } else {
    const me = getIdentity();
    await star({
      exhibitor_key: key,
      name_key: nKey,
      show: dir.show,
      show_key: dir.key,
      name: ex.name,
      booths: ex.booths,
      added_by: attributionName(me),
    });
    starred.add(key);
    starred.add(nKey);
    toast(`★ “${ex.name}” added to the hit list`);
  }
  void syncNow();
}

// ── Exhibitor detail ────────────────────────────────────────────────────────

export async function renderExhibitorDetail(root: HTMLElement, key: string): Promise<void> {
  // The key carries its own show slug (`id:asd:…` / `name:closeout:…`), so the
  // route needs one param and a deep link survives a reload.
  const showKey = key.split(':')[1] ?? '';
  const dir = await getShowDirectory(showKey);
  const ex = dir?.exhibitors.find((e) => exhibitorKey(dir.key, e) === key);

  if (!dir || !ex) {
    root.innerHTML = `<div class="empty">That exhibitor isn’t in the bundled list.<br/><a href="#/data" class="link">Back to Info</a></div>`;
    return;
  }

  const [idx, starred] = await Promise.all([buildCapturedIndex(dir), starredKeys()]);

  function paint(): void {
    const state = captureStateFor(ex!, idx);
    const on = isStarred(dir!.key, ex!, starred);
    const pills = ex!.booths.length
      ? `<div class="ex-detail__booths">${ex!.booths
          .map(
            (b) =>
              `<span class="chip chip--booth${
                state.capturedBooths.includes(b) ? ' chip--booth-done' : ''
              }">${esc(b)}${state.capturedBooths.includes(b) ? ' ✓' : ''}</span>`,
          )
          .join('')}</div>`
      : `<div class="ex-detail__nobooth">Booth numbers aren’t published for ${esc(dir!.show)} yet.</div>`;

    root.innerHTML = `
      <section class="card ex-detail">
        <a href="#/show/${encodeURIComponent(dir!.key)}" class="link">&lsaquo; ${esc(dir!.show)} directory</a>
        <h2 class="ex-detail__name">${esc(ex!.name)}</h2>
        ${pills}
        ${
          state.captured
            ? `<div class="ex-detail__flag ex-detail__flag--yes">✓ ${
                state.partial
                  ? `Partly captured — ${state.capturedBooths.length} of ${ex!.booths.length} booths logged`
                  : `Captured — ${state.records.length} record${state.records.length === 1 ? '' : 's'} on file`
              }</div>`
            : ''
        }
        <div id="ex-xref" class="ex-detail__xref"></div>
        <div class="ex-detail__actions">
          <button type="button" class="btn btn--primary" id="ex-capture">
            ${state.captured && !state.partial ? 'View booth' : 'Capture this booth'}
          </button>
          <button type="button" class="btn ex-detail__star${on ? ' ex-detail__star--on' : ''}" id="ex-star">
            ${on ? '★ On the hit list' : '☆ Add to hit list'}
          </button>
        </div>
        ${
          state.partial
            ? `<p class="ex-detail__hint">Some booths are still unlogged — “Capture this booth” will ask which one.</p>`
            : ''
        }
      </section>`;

    root.querySelector<HTMLButtonElement>('#ex-star')!.addEventListener('click', async () => {
      await toggleStar(dir!, ex!, starred);
      paint();
    });

    root.querySelector<HTMLButtonElement>('#ex-capture')!.addEventListener('click', () => {
      void captureOrView(dir!, ex!, state);
    });

    void renderCrossRef(root.querySelector<HTMLElement>('#ex-xref')!, ex!.name);
  }

  paint();
}

/**
 * The duplicate-aware action behind the detail button.
 *
 * The rule from the brief: this button must never CREATE a duplicate. Fully
 * captured → it opens the existing record (picking, when several people logged the
 * same booth). Not captured → a pre-filled Capture. Partly captured (multi-booth) →
 * ask which booth, then view or capture that one accordingly. Capturing the same
 * booth from the Capture tab is untouched and still allowed.
 */
async function captureOrView(dir: ShowDirectory, ex: Exhibitor, state: CaptureState): Promise<void> {
  const remaining = ex.booths.filter((b) => !state.capturedBooths.includes(b));

  // Fully captured (or a booth-less show already logged by name) → open the record.
  if (state.captured && !remaining.length) return openRecord(state.records);

  // Multi-booth with something left to do → which booth are you standing at?
  if (remaining.length > 1) {
    const picked = await chooseDialog<string>({
      title: 'Which booth?',
      message: `${ex.name} has ${ex.booths.length} booths at ${dir.show}.`,
      options: ex.booths.map((b) => ({
        value: b,
        label: b,
        hint: state.capturedBooths.includes(b) ? 'already captured — opens the record' : undefined,
      })),
    });
    if (!picked) return;
    if (state.capturedBooths.includes(picked)) {
      const forBooth = state.records.filter(
        (r) => r.booth_number.trim().toLowerCase() === picked.trim().toLowerCase(),
      );
      return openRecord(forBooth.length ? forBooth : state.records);
    }
    return goCapture(dir, ex, picked);
  }

  return goCapture(dir, ex, remaining[0] ?? ex.booths[0] ?? '');
}

/** Open an existing capture; several records for one booth → let the user choose. */
async function openRecord(records: BoothRecord[]): Promise<void> {
  if (!records.length) return;
  if (records.length === 1) {
    location.hash = `#/booth/${encodeURIComponent(records[0].id)}`;
    return;
  }
  const picked = await chooseDialog<string>({
    title: `${records.length} captures — open which?`,
    message: 'The same booth was logged more than once.',
    options: records.map((r) => ({
      value: r.id,
      label: `#${r.booth_number}${r.vendor_name ? ` · ${r.vendor_name}` : ''}`,
      hint: `${r.priority} · ${creatorFirstName(r.captured_by)} · ${new Date(r.captured_at).toLocaleDateString()}`,
    })),
  });
  if (picked) location.hash = `#/booth/${encodeURIComponent(picked)}`;
}

function goCapture(dir: ShowDirectory, ex: Exhibitor, booth: string): void {
  setCapturePrefill({ show: dir.show, vendor_name: ex.name, booth_number: booth });
  location.hash = '#/capture';
}

function creatorFirstName(raw: string): string {
  const v = (raw ?? '').trim();
  if (!v) return '—';
  const first = v.split(/\s+/)[0];
  return first.includes('@') ? v : first;
}

// ── SSS cross-reference ─────────────────────────────────────────────────────

/**
 * "Do we already buy from them?" — fuzzy-match the exhibitor name against the
 * cached snapshot's vendor rows (same matcher the Lookup tab uses).
 *
 * Reads only the cached snapshot, so it works offline; if no snapshot has been
 * built on this device the line simply doesn't appear. Nothing is claimed above
 * what the score supports: a strong match plus real purchase history reads as "we
 * already buy from them", a weaker one as "likely — confirm". Below MIN_SCORE we
 * hide the line entirely rather than show a shrug.
 */
const MIN_SCORE = 0.72;
const STRONG_SCORE = 0.85;

async function renderCrossRef(el: HTMLElement, exhibitorName: string): Promise<void> {
  const snap = await getSnapshot();
  if (!snap?.vendors?.length) return;

  const ranked = rankMatches(exhibitorName, snap.vendors, (v: VendorRow) => v.vendor_name || v.vendor_id, {
    limit: 1,
    threshold: MIN_SCORE,
  });
  if (!ranked.length) return;

  const { item: v, score } = ranked[0];
  const ordered = numish(v.total_spend) > 0 || (v.po_count ?? 0) > 0;
  const strong = score >= STRONG_SCORE;

  const headline = ordered
    ? strong
      ? 'We already buy from them'
      : 'Likely one of our suppliers — confirm the name'
    : strong
      ? 'Known vendor — no purchase history on file'
      : 'Possible match in our vendor records — confirm';

  el.innerHTML = `
    <div class="xref ${ordered ? 'xref--yes' : 'xref--known'}">
      <div class="xref__headline">${ordered ? '✓ ' : ''}${headline}</div>
      <a class="xref__link" href="#/vendor/${encodeURIComponent(v.vendor_id)}">
        ${esc(v.vendor_name || v.vendor_id)} <span class="xref__go">›</span>
      </a>
    </div>`;
}

function numish(v: string | number | null): number {
  if (v == null || v === '') return 0;
  const m = String(v).match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : 0;
}

// ── Hit list view ───────────────────────────────────────────────────────────

export async function renderHitListView(root: HTMLElement): Promise<void> {
  const [targets, shows] = await Promise.all([listHitList(), loadShows()]);

  if (!targets.length) {
    root.innerHTML = `
      <section class="card ex-dir">
        <a href="#/data" class="link">&lsaquo; Info</a>
        <h2 class="ex-dir__title">Hit list</h2>
        <div class="empty">Nothing starred yet.<br/>Open a show directory and tap ☆ on the booths worth working.</div>
      </section>`;
    void pullHitList().then((n) => {
      if (n > 0 && location.hash === '#/hitlist') void renderHitListView(root);
    });
    return;
  }

  // Captured state per show, so the plan shows what's already been worked. One
  // index per show that actually has starred targets — not all six.
  const dirsByKey = new Map(shows.map((s) => [s.key, s]));
  const usedShowKeys = [...new Set(targets.map((t) => t.show_key))];
  const idxByShow = new Map<string, CapturedIndex>();
  await Promise.all(
    usedShowKeys.map(async (k) => {
      const dir = dirsByKey.get(k);
      if (dir) idxByShow.set(k, await buildCapturedIndex(dir));
    }),
  );

  const groups = usedShowKeys.map((k) => ({
    key: k,
    label: dirsByKey.get(k)?.show ?? k,
    items: targets.filter((t) => t.show_key === k),
  }));

  root.innerHTML = `
    <section class="card ex-dir">
      <a href="#/data" class="link">&lsaquo; Info</a>
      <h2 class="ex-dir__title">Hit list</h2>
      <div class="ex-dir__meta">${targets.length} starred · shared with the team</div>
      ${groups
        .map(
          (g) => `
        <h3 class="ex-dir__group">${esc(g.label)} <span class="ex-dir__groupn">${g.items.length}</span></h3>
        <ul class="ex-list">${g.items
          .map((t) => {
            const idx = idxByShow.get(t.show_key);
            const state = idx
              ? captureStateFor({ name: t.name, id: null, booths: t.booths }, idx)
              : null;
            return `
            <li class="ex-row">
              <a class="ex-row__main" href="#/exhibitor/${encodeURIComponent(t.exhibitor_key)}">
                <div class="ex-row__name">${esc(t.name)}</div>
                <div class="ex-row__meta">
                  ${t.booths.length ? `<span class="chip chip--booth">${esc(t.booths[0])}${t.booths.length > 1 ? ` +${t.booths.length - 1}` : ''}</span>` : ''}
                  ${state?.captured ? `<span class="chip chip--captured">✓ ${state.partial ? 'Partly captured' : 'Captured'}</span>` : ''}
                  <span class="ex-row__by">${esc(creatorFirstName(t.added_by))}</span>
                </div>
              </a>
              <button type="button" class="ex-row__star ex-row__star--on" data-unstar="${esc(t.exhibitor_key)}"
                      aria-label="Remove from hit list">★</button>
            </li>`;
          })
          .join('')}</ul>`,
        )
        .join('')}
    </section>`;

  root.querySelectorAll<HTMLButtonElement>('[data-unstar]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const key = btn.dataset.unstar!;
      const target = targets.find((t) => t.exhibitor_key === key);
      await unstar(key);
      // The row may have been created under the name identity — clear that too.
      if (target?.name_key && target.name_key !== key) await unstar(target.name_key);
      toast(`Removed “${target?.name ?? 'exhibitor'}” from the hit list`);
      void syncNow();
      await renderHitListView(root);
    });
  });

  void pullHitList().then((n) => {
    if (n > 0 && location.hash === '#/hitlist') void renderHitListView(root);
  });
}

// ── helpers ─────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
